import {
  Button,
  Group,
  Select,
  Stack,
  Fieldset,
  Progress,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSamplingQueries } from '../../data-access/queries/sampling';
import { AnalysisType } from '../../data-access/types/analysis-type';

export interface SamplingPlanFormValues {
  contract: string;
  analysis_type: string;
  field: string | null;
  nbr_points: string;
  zoning_method: string;
}

export function CreateSamplingPlanForm({
  onSubmit,
  onCancel,
}: {
  onSubmit?: (values: SamplingPlanFormValues) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm<SamplingPlanFormValues>({
    mode: 'controlled',
    initialValues: {
      contract: '',
      analysis_type: '',
      field: null,
      nbr_points: '',
      zoning_method: '',
    },
    validate: {
      contract: (value) => (!value ? t('forms.required') : null),
      analysis_type: (value) => (!value ? t('forms.required') : null),
      nbr_points: (value) => (!value ? t('forms.required') : null),
      zoning_method: (value) => (!value ? t('forms.required') : null),
    },
  });

  const samplingQueries = useSamplingQueries();
  const formValues = useQuery({
    ...samplingQueries.plans.values({
      contract: form.values.contract,
    }),
    placeholderData: keepPreviousData,
  });

  const handleSubmit = (values: SamplingPlanFormValues) => {
    if (onSubmit) onSubmit(values);
    // Add mutation logic here if needed
  };

  form.watch('analysis_type', ({ value }) => {
    if (value === AnalysisType.DENSITY) {
      form.setFieldValue('field', null);
    }
  });

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Fieldset disabled={formValues.isFetching} variant="unstyled">
        {formValues.isFetching ? (
          <Progress
            animated
            radius={0}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
            }}
            value={100}
          />
        ) : null}
        <Stack gap="md" mt="sm">
          <Select
            data={
              formValues.data?.contract.map((contract) => ({
                value: contract.id.toString(),
                label: `${contract.farm.name} (${contract.clause})`,
              })) || []
            }
            label={t('common.societe')}
            required
            {...form.getInputProps('contract')}
          />
          <Select
            data={formValues.data?.analysis_type}
            label={t('common.type-danalyse')}
            required
            {...form.getInputProps('analysis_type')}
          />
          <Select
            data={
              formValues.data?.field.map((field) => ({
                value: field.id.toString(),
                label: field.name,
              })) || []
            }
            disabled={form.values.analysis_type === AnalysisType.DENSITY}
            label={t('common.parcelle')}
            required
            {...form.getInputProps('field')}
          />
          <Select
            data={formValues.data?.nbr_points.map((point) => ({
              value: point.value.toString(),
              label: point.label.toString(),
            }))}
            label={t('common.nombre-de-points')}
            required
            {...form.getInputProps('nbr_points')}
          />
          <Select
            data={formValues.data?.zoning_method}
            label={t('common.methode-de-zonage')}
            required
            {...form.getInputProps('zoning_method')}
          />
          <Group mt="md">
            <Button type="submit">{t('common.generate')}</Button>
            <Button color="red" onClick={onCancel} type="button">
              {t('common.cancel')}
            </Button>
          </Group>
        </Stack>
      </Fieldset>
    </form>
  );
}
